import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formatActionLabel, groupActions } from "./rolePermissionShared";

function CategorySection({
  category,
  isAllowed,
  categoryAllChecked,
  onToggleCategory,
  onTogglePermission,
  readOnly,
}) {
  const selectedCount = category.actions.filter((a) =>
    isAllowed(a.application_module_action_id)
  ).length;
  const allChecked = categoryAllChecked(category.actions);

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
          p: 2,
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
          bgcolor: "grey.50",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={500} color="primary">
            {category.category_name}
          </Typography>
          <Chip
            size="small"
            label={`${selectedCount}/${category.actions.length}`}
            color={allChecked ? "success" : "default"}
            variant="filled"
          />
        </Box>
        {!readOnly && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => onToggleCategory(category.actions, allChecked)}
            sx={{ textTransform: "none", borderRadius: 1 }}
          >
            {allChecked ? "Uncheck All" : "Check All"}
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        {category.actions.map((action) => {
          const actionId = action.application_module_action_id;
          const checked = isAllowed(actionId);

          return (
            <Box
              key={actionId}
              sx={{
                flex: "0 1 auto",
                minWidth: 140,
                p: 1.25,
                border: 1,
                borderColor: checked ? "primary.light" : "divider",
                borderRadius: 1,
                bgcolor: checked ? "action.selected" : "background.paper",
                transition: "all 0.2s",
                "&:hover": readOnly
                  ? {}
                  : {
                      bgcolor: checked ? "action.selected" : "grey.50",
                      borderColor: "primary.main",
                    },
              }}
            >
              <FormControlLabel
                sx={{ m: 0, width: "100%" }}
                control={
                  <Checkbox
                    size="small"
                    checked={checked}
                    disabled={readOnly}
                    onChange={() => onTogglePermission(actionId)}
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={500}>
                    {formatActionLabel(action)}
                  </Typography>
                }
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default function RolePermissionEditor({
  modules = [],
  permissions = [],
  onPermissionsChange,
  expandedModules = {},
  onExpandedModulesChange,
  readOnly = false,
}) {
  const isAllowed = (actionId) =>
    permissions.some(
      (p) =>
        p.application_module_action_id === actionId && p.permission_allowed
    );

  const togglePermission = (actionId) => {
    if (readOnly || !onPermissionsChange) return;
    onPermissionsChange((prev) => {
      const idx = prev.findIndex(
        (p) => p.application_module_action_id === actionId
      );
      if (idx !== -1) {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          permission_allowed: !next[idx].permission_allowed,
        };
        return next;
      }
      return [
        ...prev,
        { application_module_action_id: actionId, permission_allowed: true },
      ];
    });
  };

  const toggleCategory = (categoryActions, allChecked) => {
    if (readOnly || !onPermissionsChange) return;
    onPermissionsChange((prev) => {
      let next = [...prev];
      categoryActions.forEach((action) => {
        const actionId = action.application_module_action_id;
        const idx = next.findIndex(
          (p) => p.application_module_action_id === actionId
        );
        if (idx !== -1) {
          next[idx] = { ...next[idx], permission_allowed: !allChecked };
        } else {
          next.push({
            application_module_action_id: actionId,
            permission_allowed: !allChecked,
          });
        }
      });
      return next;
    });
  };

  const categoryAllChecked = (categoryActions) =>
    categoryActions.every((a) => isAllowed(a.application_module_action_id));

  if (!modules.length) {
    return (
      <Alert severity="info">
        No modules/actions for this application. Configure Permissions access
        first.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      {modules.map((module) => {
        const categories = groupActions(module.actions);
        const selectedCount = (module.actions || []).filter((a) =>
          isAllowed(a.application_module_action_id)
        ).length;
        const moduleKey = module.application_module_id || module.module_id;

        return (
          <Accordion
            key={moduleKey}
            expanded={Boolean(expandedModules[moduleKey])}
            onChange={() =>
              onExpandedModulesChange?.((prev) => ({
                ...prev,
                [moduleKey]: !prev[moduleKey],
              }))
            }
            sx={{
              m: 0,
              border: 1,
              borderColor: "divider",
              borderRadius: "8px !important",
              "&:before": { display: "none" },
              boxShadow: "none",
              "&.Mui-expanded": { m: 0 },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: "grey.100",
                borderRadius: 1,
                "&.Mui-expanded": {
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  width: "100%",
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  {module.module_name}
                </Typography>
                <Chip
                  size="small"
                  label={`${selectedCount} selected`}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              {categories.length === 0 ? (
                <Typography color="text.secondary">
                  No actions available for this module.
                </Typography>
              ) : (
                categories.map((cat, catIndex) => (
                  <React.Fragment key={cat.category_name}>
                    <CategorySection
                      category={cat}
                      isAllowed={isAllowed}
                      categoryAllChecked={categoryAllChecked}
                      onToggleCategory={toggleCategory}
                      onTogglePermission={togglePermission}
                      readOnly={readOnly}
                    />
                    {catIndex < categories.length - 1 ? (
                      <Divider sx={{ mb: 3 }} />
                    ) : null}
                  </React.Fragment>
                ))
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
